import React, { useEffect, useState } from "react";
import { MdKeyboardBackspace } from "react-icons/md";
import { TokenCard } from "../../components";
import { coreumCoin, evmCoins, radixCoin, StableCoin } from "../../utils/config/stableCoins";
import { activateBillingSubscription, approveERC20Token, checkApprovedToken, getNativeBalance } from "../../utils/services/smartContract";
import "./PaymentSummary.scss";
import { getBlockchainType } from "../../utils/services/auth";
import { USDToCore, initiateCoreumPayment } from "../../utils/services/coreum";
import { useSelector } from "react-redux";
import { initiateRadixPayment, usdToXRD } from "../../utils/services/radix";
import ReactLoading from "react-loading";
import { availableNetworks } from "../../utils/config/availableNetworks";
import { notify } from "../../utils/services/notification";



interface PlanDetails {
  id: string;
  amount: number;
  cost: number;
  title: string
  index: string
}

interface PaymentSummaryProps {
  setCurrentScreen: (screen: string) => void;
  selectedPlan: PlanDetails;
}

const PaymentSummary: React.FC<PaymentSummaryProps> = ({ setCurrentScreen, selectedPlan }) => {
  const [selectedCoin, setSelectedCoin] = useState<string | null>(null);
  const [planAmount, setPlanAmount] = useState<string | null>(null);
  const [gasToken, setGasToken] = useState<string | null>(null);
  const [stableCoins, setStableCoins] = useState<StableCoin[] | null>([]);
  const authStore = useSelector((store: any) => store?.auth);
  const [isLoading, setIsloading] = useState<boolean>(false);
  const currentChainStore = useSelector((store: any) => store?.currentChain);
  const [tokenStatus, setTokenStatus] = useState<string>('start');

  const addTopup = async (coinName: string, planDetails: any | null) => {
    if (planDetails) {
      setIsloading(true)
      try {
        if (getBlockchainType() === "Coreum") {
          await initiateCoreumPayment(planDetails.amount, planDetails.id, setIsloading);
        } else if (getBlockchainType() === "Radix") {
          await initiateRadixPayment(planDetails.amount, planDetails.id, setIsloading);
        }
        else {
          const isApproved = await approveERC20Token(coinName, planDetails.id, setIsloading);
          isApproved && setTokenStatus('Approved');
        }
      } catch (error) {
        setIsloading(false);
      }
    }
  };

  const checkApproved = async (coinName: string, planDetails: any | null) => {
    if (selectedCoin) {
      const allowance = await checkApprovedToken(coinName, planDetails.id, setIsloading)
      // console.log(allowance, 'allowance');
      notify('Token approved! You can now proceed with the payment.','info');
      allowance && setTokenStatus('Approved');
    } else {
      notify('Please Select a token to continue', 'info')
    }
  }

  const transferPayment = async (coinName:string, planDetails: any | null)=>{
    const allowance = await checkApprovedToken(coinName, planDetails.id, setIsloading)
    if(allowance){
      await activateBillingSubscription(coinName,planDetails.id,setIsloading);
    }else{
      notify('Token approval is still processing. Try again in a few moments','info');
    }
  }

  useEffect(() => {
    let componentMounted = true;
    if (componentMounted) {
      (async () => {
        setIsloading(true);
        if (getBlockchainType() === "Coreum") {
          setStableCoins(coreumCoin);
          setPlanAmount((await USDToCore(selectedPlan.cost)).toString() + 'CORE');
        } else if (getBlockchainType() === "Radix") {
          setStableCoins(radixCoin);
          setPlanAmount((await usdToXRD(selectedPlan.cost)).toString() + 'XRD');
        }
        else {
          const filteredCoins = evmCoins.filter(coin => coin.chain.includes(currentChainStore?.currentChain));
          setStableCoins(filteredCoins);
          setPlanAmount(selectedPlan.cost + ' USD');
          const chainInfo = availableNetworks[currentChainStore?.currentChain + ''];
          const gasToken = await getNativeBalance()
          setGasToken((Number(gasToken)?.toFixed(2) + ' ' + chainInfo.nativeCurrency.name) + '')
        }
        setIsloading(false);
      })();
    }
    return () => {
      componentMounted = false;
    };
  }, [selectedPlan, currentChainStore?.currentChain]);


  return (
    isLoading ?
      <>
        <div className="loadingContainer">
          <ReactLoading
            type={"bubbles"}
            color={"#4452FE"}
            height={667}
            width={375}
          />
        </div>

      </> :
      <div className="PaymentSummary">
        <p className="title">
          <span
            onClick={() => {
              setCurrentScreen("PaymentType");
            }}
          >
            <MdKeyboardBackspace />
          </span>
          &nbsp; Payment Summary
        </p>
        <div className="SummaryCard">
          <table>
            <tbody>
              <tr>
                <td>Your Purchase</td>
                <td>{selectedPlan?.title}</td>
              </tr>
              <tr>
                <td>Total Price</td>
                <td>{planAmount}</td>
              </tr>
            </tbody>
          </table>
          <div className="CoinContainer">
            <p>Make Your Payment From</p>
            {stableCoins &&
              stableCoins.map((coin: any, key: number) => (
                <div
                  className="coinBox"
                  key={key}
                  style={
                    selectedCoin === coin?.name
                      ? { transform: "scale(1.02)" }
                      : {}
                  }
                >
                  <TokenCard
                    {...coin}
                    size="any"
                    minBalance={selectedPlan?.cost}
                    style={{ opacity: "0.5", cursor: "auto" }}
                    setSelectedCoin={setSelectedCoin}
                  />
                </div>
              ))}
          </div>
        </div>
        <div className="buttonContainer">
          {
            authStore?.blockchain === 'EVM' && <p>*You have <span>{gasToken} Token</span>, Please Maintain Sufficient Token balance for gas fee</p>
          }

          {
            getBlockchainType() === "EVM" ? 
            <div className="ercTokenContainer">
              <p onClick={() => checkApproved(selectedCoin || '', {
                amount: selectedPlan?.cost,
                id: selectedPlan?.index,
              })}>Already Approved ? (Check Now)</p>
              {
                tokenStatus === 'Approved' ? 
                <button
                  className="fillBtn__blue"
                  disabled={!selectedCoin}
                  onClick={() => {
                    transferPayment(selectedCoin || '', {
                      amount: selectedPlan?.cost,
                      id: selectedPlan?.index,
                    });
                  }}
                >Pay Now
                </button> : 
                <button
                  className="fillBtn__blue"
                  disabled={!selectedCoin}
                  onClick={() => {
                    addTopup(selectedCoin || '', {
                      amount: selectedPlan?.cost,
                      id: selectedPlan?.index,
                    });
                  }}
                >Approve & Pay
                </button>
              }
            </div> :
             <>  
              <button
              className="fillBtn__blue"
              disabled={!selectedCoin}
              onClick={() => {
                addTopup(selectedCoin || '', {
                  amount: selectedPlan?.cost,
                  id: selectedPlan?.index,
                });
              }}
            >Buy Now
            </button>
            </>
          }
        </div>
      </div>
  );
}

export default PaymentSummary;
